import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {FormLabel, MenuItem, Grid, Autocomplete} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, iso2Date} from "@/helper/method";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import {useCreateContact, useQueryContact, useUpdateContact} from "@/service/contact.s";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CustomerPO} from "@/model/customer";
import {WriteChanceForm} from "@/pages/Chance/WriteChance";
import {isNumber} from "lodash";
import {RoleTreeVO, useQueryRoleTree} from "@/service/role.s";
import {DeptTreeVO, useQueryDeptTree} from "@/service/dept.s";
import {DictPO} from "@/model/system";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import md5 from "md5";
import {useCreateUser, useQueryUser, useUpdateUser} from "@/service/user.s";

const validationSchema = z.object({
    account: z.string({message: '请填写登录账号'}).nonempty({message: "请填写登录账号"}),
    password: z.string({message: '请填写登录密码'}).nonempty({message: "请填写登录密码"}),
    statusId: z.unknown().default(2),
    status: z.string({message: "请选择员工状态"}).default("在职"),
    username: z.string({message: '请填写姓名'}).nonempty({message: "请填写姓名"}),
    mobile: z.string({message: "请填写联系电话"}).nonempty({message: "请填写联系电话"}),
    entryTime: z.date({message: "请选择入职时间"}),
    email: z.unknown(),
    roleId: z.string({message: "请选择角色"}),
    deptId: z.number({message: "请选择部门"}),
    gender: z.number().nullish(),
    id: z.number().default(0),
    // email: z.string().email("请填写正确的邮箱格式").max(60, "邮箱长度不能超过60位").refine((val) => val.length > 0)
});

export type WriteUserForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteUser = () => {
    const {gender, userStatus} = useAllDict()
    const {id: drawerId, write} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const {mutateAsync: createUserMutate, isPending: createUserPending} = useCreateUser()
    const {mutateAsync: updateUserMutate, isPending: updateUserPending} = useUpdateUser()
    const {data: roleTreeData} = useQueryRoleTree()
    const {data: deptTreeData} = useQueryDeptTree()

    const {data: userData} = useQueryUser({id: drawerId!}, isNumber(drawerId))
    const user = userData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()
    const roles = flattenTree(roleTreeData?.data) || []
    const depts = flattenTree(deptTreeData?.data) || []

    function flattenTree(tree: any, result: any[] = [], parentId = null) {
        tree?.forEach((node: any) => {
            const newNode: any = {...node, parentId}; // 复制节点并添加parentId
            delete newNode.children; // 删除children属性，因为现在是平铺结构
            result.push(newNode); // 将节点添加到结果中

            if (node.children) {
                flattenTree(node.children, result, node.id); // 递归处理子节点
            }
        });
        return result;
    }

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteUserForm>({
        values: {...user, entryTime: iso2Date(user?.entryTime)} as WriteUserForm,
        resolver: zodResolver(validationSchema),
        defaultValues,
    });

    const onSubmit = async (values: WriteUserForm) => {
        const result = write == "add" ? await createUserMutate({...values, password: md5(values.password)}) : await updateUserMutate({
            ...values,
            id: drawerId!,
            password: md5(values.password)
        })
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid spacing={2} container>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>账号</FormLabel>
                        <TextField
                            error={!!errors?.account}
                            helperText={errors?.account?.message}
                            {...register('account')}
                            placeholder="填写账号"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>密码</FormLabel>
                        <TextField
                            error={!!errors?.password}
                            helperText={errors?.password?.message}
                            {...register('password')}
                            placeholder="填写密码"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>姓名</FormLabel>
                        <TextField
                            error={!!errors?.username}
                            helperText={errors?.username?.message}
                            {...register('username')}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 10},
                                    endAdornment: (
                                        <PermIdentityOutlinedIcon color="disabled"/>
                                    ),
                                },
                            }}
                            placeholder="填写姓名"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>联系电话</FormLabel>
                        <TextField
                            error={!!errors?.mobile}
                            helperText={errors?.mobile?.message}
                            {...register(`mobile`)}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 15},
                                    endAdornment: (
                                        <PhoneOutlinedIcon color="disabled"/>
                                    ),
                                },
                            }}
                            placeholder="填写联系电话"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="email">联系邮箱</FormLabel>
                        <TextField
                            id="email"
                            {...register(`email`)}
                            error={!!errors?.email}
                            helperText={errors?.email?.message}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                    endAdornment: (
                                        <AlternateEmailOutlinedIcon color="disabled"/>
                                    ),
                                },
                            }}
                            placeholder="填写联系邮箱"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>性别</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <TextField value={value || null} onChange={event => onChange(event.target.value)}
                                              placeholder={"选择性别"} select={true}>
                                {gender?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        }} name={`gender`}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>入职时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择入职时间"}
                                customInput={<TextField
                                    error={!!errors?.entryTime}
                                    helperText={errors?.entryTime?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"entryTime"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={userStatus}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue(`statusId`, option?.value);
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.status}
                                           helperText={errors?.status?.message}
                                           variant="outlined"
                                           placeholder={"选择状态"} {...params} />
                            )}
                        />} name={"status"}/>
                    </FormControl>
                </Grid>


                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>角色</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <TextField value={value || null} onChange={event => onChange(event.target.value)}
                                              placeholder={"选择角色"} select={true}>
                                {roles?.map((option: RoleTreeVO) => (
                                    <MenuItem key={option.roleId} value={option.roleId}>
                                        {option.roleName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        }} name={`roleId`}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>部门</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <TextField value={value || null} onChange={event => onChange(event.target.value)}
                                              placeholder={"选择部门"} select={true}>
                                {depts?.map((option: DeptTreeVO) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.deptName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        }} name={`deptId`}/>
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createUserPending || updateUserPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
